import { createContext, useContext, useState, ReactNode } from 'react';
import { announce } from '@react-aria/live-announcer';

type Priority = 'assertive' | 'polite';

interface AccessibilityContextType {
  screenReaderEnabled: boolean;
  toggleScreenReader: () => void;
  announceMessage: (message: string, priority?: Priority) => void;
}

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(
  undefined
);

let cachedVoice: SpeechSynthesisVoice | null = null;

const getBestVoice = (): SpeechSynthesisVoice | null => {
  if (cachedVoice) return cachedVoice;

  if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
    return null;
  }

  const voices = window.speechSynthesis.getVoices();
  if (!voices || voices.length === 0) return null;

  const googlePtBr = voices.find(
    (v) =>
      v.lang.toLowerCase().startsWith('pt-br') &&
      v.name.toLowerCase().includes('google')
  );
  if (googlePtBr) {
    cachedVoice = googlePtBr;
    return googlePtBr;
  }

  const anyPtBr = voices.find((v) =>
    v.lang.toLowerCase().startsWith('pt-br')
  );
  if (anyPtBr) {
    cachedVoice = anyPtBr;
    return anyPtBr;
  }

  cachedVoice = voices[0];
  return cachedVoice;
};

if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
  window.speechSynthesis.onvoiceschanged = () => {
    cachedVoice = null;
  };
}

const speak = (text: string) => {
  if (typeof window === 'undefined') return;
  if (!('speechSynthesis' in window)) return;

  const synth = window.speechSynthesis;

  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = 'pt-BR';

  const voice = getBestVoice();
  if (voice) {
    utterance.voice = voice;
  }

  utterance.rate = 0.95;
  utterance.pitch = 1.0;

  synth.cancel();
  synth.speak(utterance);
};

export const AccessibilityProvider = ({ children }: { children: ReactNode }) => {
  const [screenReaderEnabled, setScreenReaderEnabled] = useState<boolean>(() => {
    try {
      if (typeof window === 'undefined') return false;
      const saved = localStorage.getItem('screenReaderEnabled');
      return saved ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });

  const announceMessage = (
    message: string,
    priority: Priority = 'polite'
  ) => {
    if (!screenReaderEnabled) return;

    speak(message);
    announce(message, priority);
  };

  const toggleScreenReader = () => {
    const newValue = !screenReaderEnabled;
    setScreenReaderEnabled(newValue);

    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem('screenReaderEnabled', JSON.stringify(newValue));
      }
    } catch {
    }

    const statusMsg = newValue
      ? 'Leitor de tela do sistema ativado'
      : 'Leitor de tela do sistema desativado';

    speak(statusMsg);
    announce(statusMsg, 'assertive');
  };

  return (
    <AccessibilityContext.Provider
      value={{
        screenReaderEnabled,
        toggleScreenReader,
        announceMessage,
      }}
    >
      {children}
    </AccessibilityContext.Provider>
  );
};

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility deve ser usado dentro de AccessibilityProvider');
  }
  return context;
};
